import { Repeat } from "lucide-react";

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const ORDINALS = [
  { v: 1, l: "1st" },
  { v: 2, l: "2nd" },
  { v: 3, l: "3rd" },
  { v: 4, l: "4th" },
  { v: -1, l: "Last" },
];

export const DEFAULT_RECURRENCE = {
  freq: "none",
  interval: 1,
  weekdays: [],
  month_day: 1,
  setpos: [],
  custom_weekday: 4,
  end_type: "never",
  end_date: null,
  count: null,
};

const FREQS = [
  { v: "none", l: "One-time" },
  { v: "daily", l: "Daily" },
  { v: "weekly", l: "Weekly" },
  { v: "monthly", l: "Monthly" },
  { v: "custom", l: "Custom (nth weekday)" },
];

export default function RecurrenceBuilder({ value, onChange }) {
  const rec = value || DEFAULT_RECURRENCE;
  const upd = (patch) => onChange({ ...rec, ...patch });

  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return (
    <div className="border border-[var(--line)] p-4 space-y-4 bg-[#f9f9f7]">
      <div className="flex items-center gap-2">
        <Repeat className="w-4 h-4 text-[var(--accent)]" />
        <span className="label-mono text-[var(--text-2)]">Recurrence</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {FREQS.map((f) => (
          <button
            key={f.v}
            type="button"
            data-testid={`freq-${f.v}`}
            onClick={() => upd({ freq: f.v })}
            className={`h-10 px-2 text-sm border transition-colors ${
              rec.freq === f.v
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {(rec.freq === "daily" || rec.freq === "weekly" || rec.freq === "monthly") && (
        <label className="flex items-center gap-2 text-sm">
          <span className="text-[var(--text-2)]">Every</span>
          <input
            type="number"
            min="1"
            data-testid="rec-interval"
            value={rec.interval}
            onChange={(e) => upd({ interval: Math.max(1, +e.target.value || 1) })}
            className="w-16 h-9 px-2 border border-[var(--line)] bg-white text-center"
          />
          <span className="text-[var(--text-2)]">{rec.freq === "daily" ? "day(s)" : rec.freq === "weekly" ? "week(s)" : "month(s)"}</span>
        </label>
      )}

      {rec.freq === "weekly" && (
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((d, i) => (
            <button
              key={d}
              type="button"
              data-testid={`weekday-${i}`}
              onClick={() => upd({ weekdays: toggle(rec.weekdays, i) })}
              className={`w-11 h-9 text-xs font-mono border transition-colors ${
                rec.weekdays.includes(i)
                  ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                  : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      )}

      {rec.freq === "monthly" && (
        <label className="flex items-center gap-2 text-sm">
          <span className="text-[var(--text-2)]">On day</span>
          <input
            type="number"
            min="1"
            max="31"
            data-testid="rec-monthday"
            value={rec.month_day}
            onChange={(e) => upd({ month_day: Math.min(31, Math.max(1, +e.target.value || 1)) })}
            className="w-16 h-9 px-2 border border-[var(--line)] bg-white text-center"
          />
          <span className="text-[var(--text-2)]">of the month</span>
        </label>
      )}

      {rec.freq === "custom" && (
        <div className="space-y-3">
          <div>
            <span className="label-mono text-[var(--text-2)]">Which occurrences</span>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {ORDINALS.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`setpos-${o.v}`}
                  onClick={() => upd({ setpos: toggle(rec.setpos, o.v) })}
                  className={`h-9 px-3 text-sm border transition-colors ${
                    rec.setpos.includes(o.v)
                      ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                      : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="label-mono text-[var(--text-2)]">Weekday</span>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {WEEKDAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  data-testid={`custom-wd-${i}`}
                  onClick={() => upd({ custom_weekday: i })}
                  className={`w-11 h-9 text-xs font-mono border transition-colors ${
                    rec.custom_weekday === i
                      ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                      : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-[var(--text-2)] italic">
            e.g. select 2nd &amp; 4th + Fri → “Every 2nd and 4th Friday of the month”.
          </p>
        </div>
      )}

      {rec.freq !== "none" && (
        <div className="pt-3 border-t border-[var(--line)] space-y-2">
          <span className="label-mono text-[var(--text-2)]">Ends</span>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { v: "never", l: "Never" },
              { v: "on", l: "On date" },
              { v: "after", l: "After N times" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`end-${o.v}`}
                onClick={() => upd({ end_type: o.v })}
                className={`h-9 px-3 text-sm border transition-colors ${
                  rec.end_type === o.v
                    ? "bg-[#1A1A18] text-white border-[#1A1A18]"
                    : "bg-white border-[var(--line)] hover:border-[#1A1A18]"
                }`}
              >
                {o.l}
              </button>
            ))}
            {rec.end_type === "on" && (
              <input
                type="date"
                data-testid="end-date"
                value={rec.end_date || ""}
                onChange={(e) => upd({ end_date: e.target.value })}
                className="h-9 px-2 border border-[var(--line)] bg-white"
              />
            )}
            {rec.end_type === "after" && (
              <input
                type="number"
                min="1"
                data-testid="end-count"
                value={rec.count || 1}
                onChange={(e) => upd({ count: Math.max(1, +e.target.value || 1) })}
                className="w-20 h-9 px-2 border border-[var(--line)] bg-white text-center"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
